import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { STATUS_OPTIONS, CATEGORY_OPTIONS, SOURCE_OPTIONS, SENIORITY_OPTIONS, US_STATES, STATE_NAMES } from '@/components/leads/constants';

export function LeadEditDialog({ open, onClose, editForm, setEditForm, onSave }) {
  const updateField = (field, value) => setEditForm(prev => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto" data-testid="edit-lead-dialog">
        <DialogHeader><DialogTitle className="font-heading">Edit Lead</DialogTitle></DialogHeader>

        <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mt-2 mb-2 pb-1 border-b border-slate-200">Contact Details</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Full Name *</Label>
            <Input value={editForm.name || ''} onChange={e => updateField('name', e.target.value)} className="mt-1 rounded-sm" data-testid="edit-lead-name" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Title</Label>
            <Input value={editForm.title || ''} onChange={e => updateField('title', e.target.value)} placeholder="e.g. VP of Sales" className="mt-1 rounded-sm" data-testid="edit-lead-title" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Department</Label>
            <Input value={editForm.department || ''} onChange={e => updateField('department', e.target.value)} placeholder="e.g. Marketing" className="mt-1 rounded-sm" data-testid="edit-lead-department" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Seniority</Label>
            <Select value={editForm.seniority || "none"} onValueChange={v => updateField('seniority', v === 'none' ? '' : v)}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="edit-lead-seniority"><SelectValue placeholder="Select seniority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select seniority</SelectItem>
                {SENIORITY_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Primary Email</Label>
            <Input type="email" value={editForm.email || ''} onChange={e => updateField('email', e.target.value)} className="mt-1 rounded-sm" data-testid="edit-lead-email" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Alternate Email</Label>
            <Input type="email" value={editForm.alternate_email || ''} onChange={e => updateField('alternate_email', e.target.value)} className="mt-1 rounded-sm" data-testid="edit-lead-alt-email" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Primary Number</Label>
            <Input value={editForm.phone || ''} onChange={e => updateField('phone', e.target.value)} className="mt-1 rounded-sm" data-testid="edit-lead-phone" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Mobile Number</Label>
            <Input value={editForm.mobile_number || ''} onChange={e => updateField('mobile_number', e.target.value)} className="mt-1 rounded-sm" data-testid="edit-lead-mobile" />
          </div>
        </div>

        <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mt-4 mb-2 pb-1 border-b border-slate-200">Company</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Company Name</Label>
            <Input value={editForm.company || ''} onChange={e => updateField('company', e.target.value)} className="mt-1 rounded-sm" data-testid="edit-lead-company" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Website</Label>
            <Input type="url" value={editForm.website || ''} onChange={e => updateField('website', e.target.value)} placeholder="https://example.com" className="mt-1 rounded-sm" data-testid="edit-lead-website" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Size (Employees)</Label>
            <Input type="number" min="0" value={editForm.number_of_employees ?? ''} onChange={e => updateField('number_of_employees', e.target.value === '' ? null : parseInt(e.target.value, 10))} placeholder="e.g. 50" className="mt-1 rounded-sm" data-testid="edit-lead-employees" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Revenue</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
              <Input type="number" min="0" step="0.01" value={editForm.annual_revenue ?? ''} onChange={e => updateField('annual_revenue', e.target.value === '' ? null : parseFloat(e.target.value))} placeholder="e.g. 1000000" className="rounded-sm pl-7" data-testid="edit-lead-revenue" />
            </div>
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Industry</Label>
            <Input value={editForm.industry || ''} onChange={e => updateField('industry', e.target.value)} placeholder="e.g. Professional Services" className="mt-1 rounded-sm" data-testid="edit-lead-industry" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">SIC Code</Label>
            <Input value={editForm.sic_code || ''} onChange={e => updateField('sic_code', e.target.value)} placeholder="e.g. 8721" className="mt-1 rounded-sm" data-testid="edit-lead-sic" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">NAICS Code</Label>
            <Input value={editForm.naics_code || ''} onChange={e => updateField('naics_code', e.target.value)} placeholder="e.g. 541219" className="mt-1 rounded-sm" data-testid="edit-lead-naics" />
          </div>
        </div>

        <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mt-4 mb-2 pb-1 border-b border-slate-200">Address</p>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Street Address</Label>
            <Input value={editForm.address || ''} onChange={e => updateField('address', e.target.value)} className="mt-1 rounded-sm" data-testid="edit-lead-address" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">City</Label>
            <Input value={editForm.city || ''} onChange={e => updateField('city', e.target.value)} className="mt-1 rounded-sm" data-testid="edit-lead-city" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">State</Label>
            <Select value={editForm.state || "none"} onValueChange={v => updateField('state', v === 'none' ? '' : v)}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="edit-lead-state"><SelectValue placeholder="Select state" /></SelectTrigger>
              <SelectContent className="max-h-48"><SelectItem value="none">Select state</SelectItem>{US_STATES.map(s => <SelectItem key={s} value={s}>{s} — {STATE_NAMES[s]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Zip</Label>
            <Input value={editForm.zip_code || ''} onChange={e => updateField('zip_code', e.target.value)} className="mt-1 rounded-sm" data-testid="edit-lead-zip" />
          </div>
        </div>

        <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mt-4 mb-2 pb-1 border-b border-slate-200">Pipeline</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Status</Label>
            <Select value={editForm.status || 'new'} onValueChange={v => updateField('status', v)}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="edit-lead-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Lead Source</Label>
            <Select value={editForm.lead_source || "none"} onValueChange={v => updateField('lead_source', v === 'none' ? '' : v)}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="edit-lead-source"><SelectValue placeholder="Select source" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select source</SelectItem>
                {SOURCE_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Category</Label>
            <Select value={editForm.category || "none"} onValueChange={v => updateField('category', v === 'none' ? '' : v)}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="edit-lead-category"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select category</SelectItem>
                {CATEGORY_OPTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Tags (comma separated)</Label>
            <Input value={editForm.tags || ''} onChange={e => updateField('tags', e.target.value)} className="mt-1 rounded-sm" data-testid="edit-lead-tags" />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onClose(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={onSave} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm" data-testid="edit-lead-submit">Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
